/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TestCaseCampaignLinkDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 * ============================================================================
 */
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Stack } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  adminSettingsAPI,
  HandleApiActions,
} from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";

function TestCaseCampaignLinkDialog({
  open,
  testCasesToLink,
  projectId,
  variantId,
  onClose,
  onSuccess,
}) {
  const dispatch = useDispatch();

  const [activeDialog, setActiveDialog] = useState("campaign_link");
  const [campaignLinkOption, setCampaignLinkOption] = useState("link_new");
  const [campaignName, setCampaignName] = useState("");
  const [milestoneOptionsList, setMilestoneOptionsList] = useState([]);
  const [milestone, setMilestone] = useState([]);
  const [existingCampaignOptionsList, setExistingCampaignOptionsList] =
    useState([]);
  const [selectedExistingCampaignId, setSelectedExistingCampaignId] =
    useState("");
  const [enableExistingCampaignLoading, setEnableExistingCampaignLoading] =
    useState(false);

  const testCaseIds = (testCasesToLink || [])
    .filter((testCase) => testCase?.is_campaign_linked === false)
    .map((testCase) => testCase?.new_test_case_id ?? testCase?.test_case_id)
    .filter((testCaseId) => testCaseId !== undefined && testCaseId !== null);

  const clearFields = useCallback(() => {
    setActiveDialog("campaign_link");
    setCampaignLinkOption("link_new");
    setCampaignName("");
    setMilestone([]);
    setMilestoneOptionsList([]);
    setExistingCampaignOptionsList([]);
    setSelectedExistingCampaignId("");
  }, []);

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const getCampaignIdFromResponse = (resp) => {
    return (
      resp?.data?.campaign_id ||
      resp?.data?.[0]?.campaign_id ||
      resp?.data?.id ||
      resp?.data?.[0]?.id ||
      resp?.campaign_id
    );
  };

  const linkTestCasesToCampaign = async (campaignId) => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.link_test_cases,
        params: {
          campaign_id: campaignId,
          test_case_ids: testCaseIds,
        },
        show_toast: true,
      }),
    );

    if (resp.error) {
      return;
    }

    onSuccess(testCasesToLink || []);
    handleClose();
  };

  const handleAddCampaignAndLinkTestCases = async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.add_test_campaign,
        params: {
          name: campaignName,
          project_id: projectId,
          variant_id: variantId,
          milestone_id: milestone?.map((ms) => ms.value) || [],
        },
        show_toast: true,
      }),
    );

    const campaignId = getCampaignIdFromResponse(resp);

    if (resp.error || !campaignId) {
      return;
    }

    await linkTestCasesToCampaign(campaignId);
  };

  const getMilestones = useCallback(async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_vehicle_status,
        params: {
          page_number: 1,
          page_size: 1000,
          project_id: projectId,
          variant_id: variantId,
        },
      }),
    );
    if (!resp.error) {
      const filteredMilestoneOptionsList = resp?.data?.map((ms) => ({
        value: ms?.id,
        label: ms?.name,
      }));
      setMilestoneOptionsList(filteredMilestoneOptionsList || []);
    }
  }, [dispatch, projectId, variantId]);

  const handleGetExistingCampaigns = useCallback(async () => {
    setEnableExistingCampaignLoading(true);
    const resp = await dispatch(
      HandleApiActions({
        ...testCasesActions.get_test_campaigns,
        params: {
          page_number: 1,
          page_size: 1000,
          project_id: projectId,
          variant_id: variantId,
        },
        show_toast: false,
      }),
    );
    setEnableExistingCampaignLoading(false);

    if (resp.error) {
      setExistingCampaignOptionsList([]);
      setCampaignLinkOption("link_new");
      return;
    }

    const filteredCampaignOptionsList = resp?.data?.map((campaign) => ({
      value: campaign?.campaign_id,
      label: campaign?.name,
    }));
    const campaignOptions = filteredCampaignOptionsList || [];

    setExistingCampaignOptionsList(campaignOptions);
    setCampaignLinkOption(
      campaignOptions.length > 0 ? "link_existing" : "link_new",
    );
  }, [dispatch, projectId, variantId]);

  useEffect(() => {
    if (open) {
      setActiveDialog("campaign_link");
      setCampaignLinkOption("link_new");
      setSelectedExistingCampaignId("");
    }
  }, [open]);

  useEffect(() => {
    console.log("---on open campaign link dialog---", projectId, variantId);
    if (open && projectId && variantId) {
      handleGetExistingCampaigns();
    }
  }, [handleGetExistingCampaigns, open, projectId, variantId]);

  useEffect(() => {
    if (open && activeDialog === "add_campaign" && projectId && variantId) {
      getMilestones();
    }
  }, [activeDialog, getMilestones, open, projectId, variantId]);

  const campaignLinkOptions =
    existingCampaignOptionsList.length > 0
      ? [
        { value: "link_existing", label: "Link Existing" },
        { value: "link_new", label: "Link to New Campaign" },
      ]
      : [{ value: "link_new", label: "New Campaign" }];

  return (
    <>
      <CustomDialogBoxV2
        open={open && activeDialog === "campaign_link"}
        onClose={handleClose}
        title={"Campaign Link"}
        successButtonName={"Confirm"}
        rejectButtonName={"Close"}
        onClickSuccess={() => {
          setActiveDialog(
            campaignLinkOption === "link_new"
              ? "add_campaign"
              : "existing_campaign",
          );
        }}
        onDisabled={enableExistingCampaignLoading}
        actionButtonStyle={"continuous"}
        onClickReject={handleClose}
      >
        <Stack spacing={2} mb={2} sx={{ minWidth: "290px", maxWidth: "290px" }}>
          <CustomRadioButtonV2
            value={campaignLinkOption}
            onChange={(e) => {
              setCampaignLinkOption(e.target.value);
            }}
            data={campaignLinkOptions}
            displayDirection="column"
          />
        </Stack>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        open={open && activeDialog === "add_campaign"}
        onClose={handleClose}
        title={"Add Campaign"}
        successButtonName={"Add"}
        rejectButtonName={"Close"}
        onClickSuccess={handleAddCampaignAndLinkTestCases}
        onDisabled={
          campaignName?.trim() === "" || testCaseIds.length === 0 || milestone.length === 0
        }
        actionButtonStyle={"continuous"}
        onClickReject={handleClose}
      >
        <Stack spacing={2} mb={2} sx={{ minWidth: "290px", maxWidth: "290px" }}>
          <CustomFormTextFieldV2
            label="Campaign Name"
            value={campaignName}
            setValue={setCampaignName}
            required={true}
          />
          <CustomMultiSelectV2
            label="Milestone"
            required={true}
            optionsList={milestoneOptionsList}
            selectedValues={milestone}
            setSelectedValues={setMilestone}
          />
        </Stack>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        open={open && activeDialog === "existing_campaign"}
        onClose={handleClose}
        title={"Link Existing Campaign"}
        successButtonName={"Submit"}
        rejectButtonName={"Close"}
        onClickSuccess={() => linkTestCasesToCampaign(selectedExistingCampaignId)}
        onDisabled={!selectedExistingCampaignId || testCaseIds.length === 0}
        actionButtonStyle={"continuous"}
        onClickReject={handleClose}
      >
       
        <Stack spacing={2} mb={2} sx={{ minWidth: "290px", maxWidth: "290px" }}>
          <CustomSelectV2
            size="small"
            label="Campaign"
            required={true}
            enableSearch={true}
            enableLoading={enableExistingCampaignLoading}
            optionsList={existingCampaignOptionsList}
            selectedValue={selectedExistingCampaignId}
            handleOnChange={(e) => {
              setSelectedExistingCampaignId(e.target.value);
            }}
            disablePortal={false}
            sltFldStyle={{ width: "270px" }}
          />
        </Stack>
      </CustomDialogBoxV2>
    </>
  );
}

TestCaseCampaignLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  testCasesToLink: PropTypes.array,
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  variantId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default TestCaseCampaignLinkDialog;
